import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LuCheck, LuCreditCard, LuReceipt, LuSparkles, LuX } from "react-icons/lu";
import { setShowPro } from "../../store/actions/actions";
import { Btn, Card, Badge, Note, Dialog, Empty, Skel } from "../components/Ui";
import "./Subscription.css";

/* Feature copy carried over from the legacy subscription page. */
const PRO_FEATURES = [
  "Unlimited job applications",
  "Unlimited mock interviews",
  "5 interview practice tests",
  "Unlock full-time jobs",
];

const BASIC_FEATURES = [
  "Job search and matching",
  "Resume upload",
  "Limited monthly usage",
];

/* Every plan card used to render PRO_FEATURES verbatim, so the dearer Auto
   Apply tier listed exactly what the cheaper one did and gave no reason to
   upgrade. The extra line is the plan's own stated benefit, not new copy. */
const AUTO_APPLY_FEATURE = "Applications submitted automatically for you";
const isAutoApplyPlan = (plan) =>
  /auto[\s-]?apply/i.test(`${plan?.product_name || ""} ${plan?.product_description || ""}`);
const featuresFor = (plan) =>
  isAutoApplyPlan(plan) ? [AUTO_APPLY_FEATURE, ...PRO_FEATURES] : PRO_FEATURES;

function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
}

/* Same day/hour math as the legacy page. */
function calculateTimeRemaining(expiryDate) {
  const now = new Date();
  const expiry = new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return "";
  const timeDiff = expiry - now;
  if (timeDiff <= 0) return "Expired";
  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return `${days} ${days > 1 ? "days" : "day"} and ${hours} hours`;
}

function PlanSkeleton() {
  return (
    <section className="ld-glass sub-plan">
      <Skel w={90} h={12} />
      <Skel w={130} h={30} />
      <Skel h={12} />
      <Skel w="82%" h={12} />
      <Skel w="70%" h={12} />
      <Skel h={44} r={999} className="sub-cta" />
    </section>
  );
}

export default function Subscription() {
  const dispatch = useDispatch();
  const userEmail = useSelector((s) => s.auth.auth.email);
  const authSubscription = useSelector((s) => s.auth.auth.subscription);
  const { membershipData, subscriptionNextAction, profileData } = useSelector((s) => s.profile);

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [redirecting, setRedirecting] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [cancelledNotice, setCancelledNotice] = useState(false);

  /* The shell's upgrade CTA sets the legacy showPro flag before routing here.
     This page now fulfils that intent, so clear the stale modal flag. */
  useEffect(() => {
    dispatch(setShowPro(false));
  }, [dispatch]);

  /* Same refresh the legacy subscription page performs on mount: it needs
     user_details (plan, status, expiry) which the shell bootstrap does not
     keep in the store. Endpoint, payload and headers are identical. */
  const fetchSubscriptionData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await fetch(
        "https://us-east1-foursssolutions.cloudfunctions.net/send_resume_details_front_end_v2",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ emailid: userEmail }),
        }
      );
      const data = await response.json();
      setDetails(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching subscription data:", error);
      setLoadError("We could not load your subscription details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  useEffect(() => {
    if (userEmail !== "") fetchSubscriptionData();
  }, [userEmail, fetchSubscriptionData]);

  /* ---------- derived state (mirrors legacy decisions) ---------- */
  /* The shell bootstrap already fetched this exact endpoint and put the record
     in the store, so blocking the whole page on a second identical call just
     made Subscription feel slow. Render from the store at once; the refetch
     below only swaps in fresher values when it lands. */
  const storedDetails = profileData && Object.keys(profileData).length ? profileData : null;
  const userDetails =
    (details && Array.isArray(details.user_details) ? details.user_details[0] : null) || storedDetails;

  const reduxPlans = Array.isArray(membershipData) ? membershipData : [];
  const fetchedPlans = details && Array.isArray(details.product_details) ? details.product_details : [];
  const paidPlans = reduxPlans.length > 0 ? reduxPlans : fetchedPlans;

  const proUser = subscriptionNextAction === "Pro User";
  /* India billing isn't wired yet (Stripe is USD-only + India needs GST/e-mandate),
     so don't show Indian users USD plans + a checkout that would fail/charge in $.
     Show a "coming soon" card instead until INR pricing is set up. */
  const isIndia = String(profileData?.country || "").trim().toLowerCase() === "india";
  const isPaidAccount = Boolean(authSubscription) && String(authSubscription).toLowerCase() !== "free";
  const manageForward = proUser || isPaidAccount;
  const expiringSoon = userDetails ? userDetails.subscription_status === "expiring soon" : false;
  const currentCode = (userDetails && (userDetails.subscribed_product_code || userDetails.subscription_plan)) || "";
  const currentPaidPlan = paidPlans.find((p) => p.product_code === currentCode) || null;
  /* a paid subscriber (Pro-User next action, a non-free auth plan, a paid
     product code, or a matched paid plan) is on a paid plan — the old check
     only looked at subscription_plan and mislabeled real Pro users as "Basic" */
  const currentPlanName =
    proUser || isPaidAccount || currentCode === "PC_101" || currentPaidPlan ? "Pro plan" : "Basic plan";
  const recommendedPlan = paidPlans.find((p) => p.product_code === "PC_101") || paidPlans[0] || null;
  const recommendedCode = recommendedPlan ? recommendedPlan.product_code : "";
  const plansLoading = loading && paidPlans.length === 0;

  /* ---------- purchase (legacy create_payment_request_v2 flow) ---------- */
  const startCheckout = async (productCode, key) => {
    if (redirecting) return;
    const redirectKey = key || productCode;
    setRedirecting(redirectKey);
    try {
      const queryPro = {
        user_email: userEmail,
        product_code: productCode,
      };
      const response = await fetch(
        "https://us-east1-foursssolutions.cloudfunctions.net/create_payment_request_v2",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(queryPro),
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.statusText}`);
      }
      const data = await response.json();
      window.location.href = `${data.payment_link}`;
      /* keep the redirecting state on: the browser is navigating away */
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("File Error:", error);
      setRedirecting(null);
      toast.error("We could not start checkout. Please try again.");
    }
  };

  /* Legacy renew payload: membershipData.product_code || "PC_101". */
  const handleRenew = () => startCheckout(membershipData.product_code || "PC_101", "renew");

  /* ---------- cancellation (legacy stripe_subscription_cancellation_v2) ---------- */
  const handleCancelSubscription = async () => {
    setCancelling(true);
    setCancelError("");
    try {
      const response = await fetch(
        "https://us-east1-foursssolutions.cloudfunctions.net/stripe_subscription_cancellation_v2",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_email: userEmail,
            product_code: (userDetails && userDetails.subscribed_product_code) || "PC_101",
          }),
        }
      );
      await response.json();
      setCancelOpen(false);
      setCancelledNotice(true);
      toast.success("Your subscription has been cancelled. Your plan stays active until the end of the current period.");
      fetchSubscriptionData();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error canceling subscription:", error);
      setCancelError("We could not cancel your subscription. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  /* ---------- Stripe billing portal (invoices, receipts, payment methods) ---------- */
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const openBillingPortal = async () => {
    setPortalLoading(true);
    setPortalError("");
    try {
      const response = await fetch(
        "https://us-east1-foursssolutions.cloudfunctions.net/stripe_billing_portal_v2",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email_id: userEmail }),
        }
      );
      const data = await response.json();
      if (response.ok && data.url) {
        window.open(data.url, "_blank", "noopener");
      } else {
        setPortalError(
          data?.message === "No billing history found for this account"
            ? "No billing history found for this account yet."
            : "We could not open your billing history. Please try again."
        );
      }
    } catch (err) {
      console.error("Error opening billing portal:", err);
      setPortalError("We could not open your billing history. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  /* ---------- pieces (Lovable _app.subscription.tsx layout) ---------- */
  /* product_name in the DB is upsell copy ("Upgrade to Pro for enhanced job
     search features!") — on the plan the user already OWNS, strip the sales
     framing so the card reads as their plan, not another pitch */
  const planDisplayName = (plan, isCurrent) => {
    const raw = String(plan.product_name || "");
    if (!isCurrent) return raw;
    const cleaned = raw
      .replace(/^upgrade to\s+/i, "")
      .replace(/\s+for enhanced.*$/i, "")
      .replace(/!+\s*$/, "")
      .trim();
    return cleaned ? `${cleaned} plan` : raw;
  };

  const renderPaidPlan = (plan) => {
    const isCurrent = manageForward && plan.product_code === currentCode;
    const isRecommended = plan.product_code === recommendedCode;
    const isRedirecting = redirecting === plan.product_code;
    return (
      <section
        key={plan.product_code}
        className={`ld-glass ld-tile-hover sub-plan${isRecommended ? " pro" : ""}${isCurrent ? " current" : ""}`}
      >
        <div className="sub-plan-head">
          <h3 className="sub-plan-name">{planDisplayName(plan, isCurrent)}</h3>
          {isRecommended ? <LuSparkles size={16} className="sub-spark" /> : null}
          {isCurrent ? (
            <Badge tone="accent">Current</Badge>
          ) : isRecommended ? (
            <Badge tone="plain">Recommended</Badge>
          ) : null}
        </div>
        <div className="sub-price">
          <span className="sub-price-n">${plan.product_price}</span>
          <span className="t-sm">per month</span>
        </div>
        {plan.product_description ? <p className="t-sm">{plan.product_description}</p> : null}
        <ul className="sub-features">
          {featuresFor(plan).map((f) => (
            <li key={f}>
              <LuCheck size={15} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <div className="sub-cta">
          {isCurrent ? (
            <Btn variant="ghost" className="btn-pill" full disabled>
              Current plan
            </Btn>
          ) : (
            <Btn
              variant={isRecommended ? "mint" : "ghost"}
              className="btn-pill"
              full
              loading={isRedirecting}
              disabled={Boolean(redirecting) && !isRedirecting}
              onClick={() => startCheckout(plan.product_code)}
            >
              {isRedirecting ? "Redirecting to secure checkout…" : isRecommended ? "Upgrade to Pro" : "Upgrade now"}
            </Btn>
          )}
        </div>
      </section>
    );
  };

  const renderBasicPlan = () => (
    <section key="basic" className="ld-glass sub-plan current">
      <div className="sub-plan-head">
        <h3 className="sub-plan-name">Basic</h3>
        <Badge tone="accent">Current</Badge>
      </div>
      <div className="sub-price">
        <span className="sub-price-n">$0</span>
        <span className="t-sm">forever</span>
      </div>
      <p className="t-sm">Core tools to explore CareerSavvy at your own pace.</p>
      <ul className="sub-features">
        {BASIC_FEATURES.map((f) => (
          <li key={f}>
            <LuCheck size={15} />
            <span>{f}</span>
          </li>
        ))}
        {PRO_FEATURES.map((f) => (
          <li key={f} className="off">
            <LuX size={15} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="sub-cta">
        <Btn variant="ghost" className="btn-pill" full disabled>
          Current plan
        </Btn>
      </div>
    </section>
  );

  const renderIndiaComingSoon = () => (
    <section key="india-soon" className="ld-glass sub-plan pro">
      <div className="sub-plan-head">
        <h3 className="sub-plan-name">Pro — coming soon in India</h3>
        <Badge tone="plain">India</Badge>
      </div>
      <div className="sub-price">
        <span className="sub-price-n" style={{ fontSize: 26 }}>₹</span>
        <span className="t-sm">pricing in rupees, soon</span>
      </div>
      <p className="t-sm">
        We're setting up India-specific pricing and payments in rupees. Your free features stay on — Pro upgrades
        for India will be available here shortly.
      </p>
      <div className="sub-cta">
        <Btn variant="ghost" className="btn-pill" full disabled>
          Coming soon
        </Btn>
      </div>
    </section>
  );

  const planCards = [];
  if (!manageForward) planCards.push(renderBasicPlan());
  if (plansLoading) {
    planCards.push(<PlanSkeleton key="skel-1" />, <PlanSkeleton key="skel-2" />);
  } else if (isIndia && !manageForward) {
    planCards.push(renderIndiaComingSoon());
  } else {
    paidPlans.slice(0, manageForward ? 3 : 2).forEach((plan) => planCards.push(renderPaidPlan(plan)));
  }
  const gridClass = planCards.length >= 3 ? "grid-3" : "grid-2";

  const statusBadge = userDetails ? (
    expiringSoon ? (
      <Badge tone="amber">Expiring soon</Badge>
    ) : (
      <Badge tone="accent">Active</Badge>
    )
  ) : null;

  const remaining = userDetails && userDetails.service_expiry_date
    ? calculateTimeRemaining(userDetails.service_expiry_date)
    : "";

  /* Top overview panel — Lovable's plan summary section with meta tiles. */
  const summarySection = (
    <section className="ld-glass sub-summary">
      <div className="sub-summary-head">
        <span className="sub-ic">
          <LuCreditCard size={19} />
        </span>
        <div className="grow">
          <h2 className="sub-summary-title">{manageForward ? currentPlanName : "Basic plan"}</h2>
          <p className="t-sm">
            {manageForward
              ? "Billing and renewal details for your account."
              : "Free forever, with limited monthly usage."}
          </p>
        </div>
        {manageForward ? statusBadge : <Badge>Basic</Badge>}
      </div>

      {manageForward ? (
        loading && !userDetails ? (
          <div className="sub-meta">
            {[1, 2, 3].map((i) => (
              <Card key={i} pad={false} className="sub-meta-tile" style={{ borderRadius: 18 }}>
                <div className="stack-2">
                  <Skel w={80} h={11} />
                  <Skel w={120} h={14} />
                </div>
              </Card>
            ))}
          </div>
        ) : userDetails ? (
          <div className="sub-meta">
            <Card pad={false} className="sub-meta-tile" style={{ borderRadius: 18 }}>
              <p className="t-xs">Current plan</p>
              <p className="sub-meta-v">{currentPlanName}</p>
              {currentPaidPlan ? <p className="t-xs">${currentPaidPlan.product_price} per month</p> : null}
            </Card>
            <Card pad={false} className="sub-meta-tile" style={{ borderRadius: 18 }}>
              <p className="t-xs">Plan ends on</p>
              <p className="sub-meta-v">
                {userDetails.service_expiry_date ? formatDate(userDetails.service_expiry_date) : "Not available"}
              </p>
              {remaining ? (
                <p className="t-xs">{remaining === "Expired" ? "Expired" : `${remaining} remaining`}</p>
              ) : null}
            </Card>
            <Card pad={false} className="sub-meta-tile" style={{ borderRadius: 18 }}>
              <p className="t-xs">Next billing action</p>
              <p className="sub-meta-v">{subscriptionNextAction || "None scheduled"}</p>
            </Card>
          </div>
        ) : (
          <p className="t-sm mt-3">Subscription details are unavailable right now.</p>
        )
      ) : subscriptionNextAction ? (
        <p className="t-xs mt-2">Next billing action: {subscriptionNextAction}</p>
      ) : null}

      {cancelledNotice ? (
        <div className="mt-3">
          <Note tone="accent">
            Billing on your card has been cancelled. Your plan stays active until the end of the current period.
          </Note>
        </div>
      ) : null}
    </section>
  );

  /* Bottom billing panel — Lovable's Billing section, carrying the legacy
     renew (expiring soon) and cancel branches. */
  const billingSection = proUser ? (
    <section className="ld-glass sub-section">
      <h3 className="sub-section-title">Billing</h3>
      {expiringSoon ? (
        <>
          <div className="mt-2">
            <Note tone="amber">Your plan is expiring soon. Renew now to keep your pro access.</Note>
          </div>
          <Btn
            variant="mint"
            className="btn-pill mt-3"
            loading={redirecting === "renew"}
            disabled={Boolean(redirecting) && redirecting !== "renew"}
            onClick={handleRenew}
          >
            {redirecting === "renew" ? "Redirecting to secure checkout…" : "Renew subscription"}
          </Btn>
        </>
      ) : (
        <>
          <p className="t-sm mt-1">Cancel any time. You keep Pro access until the end of the current period.</p>
          <div className="row mt-3" style={{ gap: 10, flexWrap: "wrap" }}>
            <Btn
              variant="soft"
              className="btn-pill"
              loading={portalLoading}
              onClick={openBillingPortal}
            >
              <LuReceipt size={15} /> Invoices & billing history
            </Btn>
            <Btn
              variant="ghost"
              className="btn-pill"
              onClick={() => {
                setCancelError("");
                setCancelOpen(true);
              }}
            >
              Cancel subscription
            </Btn>
          </div>
          {portalError ? (
            <p className="t-xs mt-2" style={{ color: "var(--danger, inherit)" }}>
              {portalError}
            </p>
          ) : null}
        </>
      )}
    </section>
  ) : null;

  const plansSection =
    !plansLoading && planCards.length === 0 ? (
      <Card pad={false}>
        <Empty
          icon={<LuCreditCard size={22} />}
          title="Plans are unavailable"
          sub="We could not load subscription plans right now."
          action={
            <Btn variant="ghost" size="sm" onClick={fetchSubscriptionData}>
              Try again
            </Btn>
          }
        />
      </Card>
    ) : (
      <div className="stack-3">
        <div className={gridClass}>{planCards}</div>
        {!plansLoading && !manageForward && paidPlans.length === 0 ? (
          <Note tone="info">Paid plans are unavailable right now. Please try again in a moment.</Note>
        ) : null}
      </div>
    );

  /* ---------- page ---------- */
  return (
    <div className="stack-4 sub-page">
      {/* Page title + subtitle live in the top bar now (no duplicate h1 here). */}

      {loadError ? (
        <Note tone="danger">
          <span className="grow">{loadError}</span>
          <Btn variant="ghost" size="sm" onClick={fetchSubscriptionData}>
            Retry
          </Btn>
        </Note>
      ) : null}

      {redirecting ? <Note tone="accent">Redirecting to secure checkout…</Note> : null}

      {/* Billing sat in the narrow left column under the summary, which pushed
          invoices and cancellation below the fold. Full-width stack instead,
          with billing directly under the plan you are being billed for. */}
      <div className="sub-stack">
        {summarySection}
        {billingSection}

        <div>
          {manageForward ? <h2 className="t-h2 mb-2">Plans</h2> : null}
          {plansSection}
        </div>
      </div>

      <Dialog open={cancelOpen} onClose={() => !cancelling && setCancelOpen(false)}>
        <h3 className="t-h3">Cancel your subscription?</h3>
        <p className="t-sm mt-2">
          Billing on your card will be stopped. Your plan stays active until the end of the current billing period,
          and you keep pro access until then.
        </p>
        {cancelError ? (
          <div className="mt-2">
            <Note tone="danger">{cancelError}</Note>
          </div>
        ) : null}
        <div className="row mt-3" style={{ justifyContent: "flex-end" }}>
          <Btn variant="ghost" className="btn-pill" disabled={cancelling} onClick={() => setCancelOpen(false)}>
            Keep my plan
          </Btn>
          <Btn variant="danger" className="btn-pill" loading={cancelling} onClick={handleCancelSubscription}>
            Cancel subscription
          </Btn>
        </div>
      </Dialog>
    </div>
  );
}
